// src/components/DateTimeClock.js
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const DateTimeClock = ({ 
  darkMode = false, 
  showSeconds = true, 
  militaryTime = false,
  className = ''
}) => {
  const [currentDateTime, setCurrentDateTime] = useState(() => new Date());
  const [error, setError] = useState(null);

  // Memoized formatting functions
  const formatTime = useCallback((date) => {
    try {
      return date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: showSeconds ? '2-digit' : undefined,
        hour12: !militaryTime
      });
    } catch (e) {
      setError('Time format error');
      return '--:--:--';
    }
  }, [showSeconds, militaryTime]);

  const formatDate = useCallback((date) => {
    try {
      return date.toLocaleDateString([], { 
        weekday: 'short', 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch (e) {
      setError('Date format error');
      return '--- --, ----';
    }
  }, []);

  useEffect(() => {
    let timer;
    try {
      timer = setInterval(() => {
        setCurrentDateTime(new Date());
      }, 1000);

      // Immediate sync check
      const now = new Date();
      if (Math.abs(now - currentDateTime) > 2000) {
        setCurrentDateTime(now);
      }
    } catch (e) {
      setError('Timer initialization failed');
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [currentDateTime]);

  // Fallback display if errors occur
  if (error) {
    return (
      <div className={`topbar-datetime error-fallback ${className}`} data-darkmode={darkMode}>
        <span className="datetime-clock__error">System Clock</span>
      </div>
    );
  }

  return (
    <div className={`topbar-datetime ${className}`} data-darkmode={darkMode}>
      <span className="datetime-clock__date">{formatDate(currentDateTime)}</span>
      <span className="datetime-clock__time">{formatTime(currentDateTime)}</span>
    </div>
  );
};

DateTimeClock.propTypes = {
  darkMode: PropTypes.bool,
  showSeconds: PropTypes.bool,
  militaryTime: PropTypes.bool,
  className: PropTypes.string
};

DateTimeClock.defaultProps = {
  darkMode: false,
  showSeconds: true,
  militaryTime: false,
  className: ''
};

export default React.memo(DateTimeClock);